/**
 * Types + helpers for the Customer Feedback report.
 *
 * Data flows: React → Worker (/api/reports/feedback) → Supabase. All
 * aggregation happens in Postgres (customer_feedback_report()); the browser
 * only shapes and renders.
 */
import { apiFetch } from './api';
import { businessYesterdayISO } from './dates';

export interface FeedbackTotals {
  total_ratings: number;
  avg_rating: number | null;
  with_comment: number;
  low_ratings: number;
  completed_stops: number;
}

export interface FeedbackDistRow {
  stars: number; // 1..5
  count: number;
}

export interface FeedbackLeader {
  driver: string;
  ratings: number;
  avg_rating: number;
  low_ratings: number;
}

export interface FeedbackCoaching {
  driver: string;
  low_ratings: number;
  total_ratings: number;
  low_pct: number;
}

export interface FeedbackComment {
  driver: string;
  stars: number;
  feedback: string;
  created: string;
  city: string | null;
  province: string | null;
  stop_ref: string | null;
}

export interface FeedbackReport {
  totals: FeedbackTotals;
  distribution: FeedbackDistRow[];
  leaderboard: FeedbackLeader[];
  coaching: FeedbackCoaching[];
  comments: FeedbackComment[];
  bounds: { mn: string | null; mx: string | null };
}

export type FeedbackRange = 'all' | 'yesterday' | 'last7' | 'last30' | 'last90';

export const RANGE_LABELS: Record<FeedbackRange, string> = {
  yesterday: 'Yesterday',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  last90: 'Last 90 days',
  all: 'All time',
};

/** Convert a UI range into from/to date bounds anchored to the latest feedback day. */
export function rangeToDates(range: FeedbackRange, latestISO: string | null): {
  from: string | null;
  to: string | null;
} {
  if (range === 'all') return { from: null, to: null };
  // "Yesterday" is the real calendar day before today (business timezone),
  // matched on trip_date — independent of the latest day present in the data.
  if (range === 'yesterday') {
    const y = businessYesterdayISO();
    return { from: y, to: y };
  }
  if (!latestISO) return { from: null, to: null };
  const latest = new Date(`${latestISO}T00:00:00Z`);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const minus = (days: number) => {
    const x = new Date(latest);
    x.setUTCDate(x.getUTCDate() - days);
    return iso(x);
  };
  const span = range === 'last7' ? 6 : range === 'last30' ? 29 : 89;
  return { from: minus(span), to: latestISO };
}

function queryString(params: Record<string, string | null>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function fetchFeedback(range: FeedbackRange, latestISO: string | null): Promise<FeedbackReport> {
  const { from, to } = rangeToDates(range, latestISO);
  return apiFetch<FeedbackReport>(`/api/reports/feedback${queryString({ from, to })}`);
}

export function pct(n: number, d: number): number {
  return d ? (n / d) * 100 : 0;
}

/** Render the n-th position of a five-star row: full / empty. */
export function starRow(rating: number): boolean[] {
  const filled = Math.round(rating);
  return [1, 2, 3, 4, 5].map((i) => i <= filled);
}

export function formatFeedbackDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Australia/Sydney',
  });
}
